import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import './CheckinPage.css';

type Step = 'phone' | 'checkedin' | 'review' | 'google-prompt' | 'done';

interface BookingInfo {
  id: string;
  customerName: string;
  serviceType: string;
  time: string;
  staffName: string;
  staffId?: string;
  checkinTime?: string;
}

const API = import.meta.env.VITE_API_URL || 'https://backend-sable-five-58.vercel.app/api';

const StarRating: React.FC<{ value: number; onChange: (v: number) => void }> = ({ value, onChange }) => (
  <div className="star-row">
    {[1, 2, 3, 4, 5].map(s => (
      <button
        key={s}
        className={`star-btn ${s <= value ? 'active' : ''}`}
        onClick={() => onChange(s)}
        type="button"
        aria-label={`${s} star`}
      >★</button>
    ))}
  </div>
);

const CheckinPage: React.FC = () => {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const T = (en: string, vi: string) => language === 'vi' ? vi : en;
  const [step, setStep] = useState<Step>('phone');
  const [phone, setPhone] = useState('');
  const [booking, setBooking] = useState<BookingInfo | null>(null);
  const [businessName, setBusinessName] = useState('');
  const [googleMapsUrl, setGoogleMapsUrl] = useState<string | null>(null);
  const [rating, setRating] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const handleAutomaticallyCheckout = async (currentPhone: string) => {
    try {
      const res = await fetch(`${API}/public/checkout/${code}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: currentPhone, localDate: new Date().toLocaleDateString('en-CA') }),
      });
      const data = await res.json();
      if (data.success) {
        setBooking(data.booking);
        setBusinessName(data.businessName || businessName);
        setGoogleMapsUrl(data.googleMapsUrl || null);
        setStep('review');
      } else {
        setError(T('Checkout error. Please try again.', 'Lỗi khi tự động checkout.'));
      }
    } catch {
      setError(T('Connection error. Please try again.', 'Lỗi kết nối checkout. Vui lòng thử lại.'));
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!phone.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/public/checkin/${code}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: phone.trim(), localDate: new Date().toLocaleDateString('en-CA') }),
      });
      const data = await res.json();

      if (data.alreadyCheckedIn) {
        setBusinessName(data.businessName || '');
        await handleAutomaticallyCheckout(phone.trim());
        return;
      }

      if (data.success) {
        setBooking(data.booking);
        setBusinessName(data.businessName || '');
        setStep('checkedin');
        return;
      }

      // No appointment found - redirect to online booking
      if (code) {
        navigate(`/book/${code}?phone=${encodeURIComponent(phone.trim())}`);
        return;
      }

      setError(T('No appointment found. Please book online.', 'Không tìm thấy lịch hẹn. Vui lòng đặt lịch online.'));
    } catch {
      setError(T('Connection error. Please try again.', 'Lỗi kết nối. Vui lòng thử lại.'));
    } finally {
      setLoading(false);
    }
  };

  const handleReviewSubmit = async () => {
    if (rating === 0) { setError(T('Please select a star rating.', 'Vui lòng chọn số sao đánh giá.')); return; }
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/public/review/${code}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          bookingId: booking?.id || null,
          customerName: booking?.customerName || 'Anonymous',
          customerPhone: phone,
          rating,
          feedback,
          staffId: booking?.staffId || '',
          staffName: booking?.staffName || '',
          serviceName: booking?.serviceType || '',
        }),
      });
      const data = await res.json();
      if (data.success) {
        if (rating === 5 && googleMapsUrl) {
          setStep('google-prompt');
        } else {
          setStep('done');
        }
      } else {
        setError(data.message || T('Error submitting review.', 'Lỗi khi gửi đánh giá.'));
      }
    } catch {
      setError(T('Connection error.', 'Lỗi kết nối.'));
    } finally {
      setLoading(false);
    }
  };

  const handleCopyAndOpenMaps = () => {
    if (!googleMapsUrl) return;
    const reviewText = feedback || `${rating} stars - Great experience at ${businessName}!`;
    navigator.clipboard.writeText(reviewText).then(() => {
      setCopied(true);
      setTimeout(() => {
        // Open Google Maps review page after a short delay so user sees "Copied!"
        window.open(googleMapsUrl, '_blank');
        setStep('done');
      }, 1200);
    }).catch(() => {
      // Clipboard failed (e.g. mobile browser) - just open Maps directly
      window.open(googleMapsUrl, '_blank');
      setStep('done');
    });
  };

  const reset = () => {
    setStep('phone');
    setPhone('');
    setRating(0);
    setFeedback('');
    setBooking(null);
    setError(null);
    setCopied(false);
    setGoogleMapsUrl(null);
  };

  const steps: Step[] = ['phone', 'checkedin', 'review', 'done'];

  return (
    <div className="ci-page">
      <div className="ci-card">
        {/* Header */}
        <div className="ci-header">
          <div className="ci-logo">✂️</div>
          <h1 className="ci-business">{businessName || t('checkin.title')}</h1>
          <div className="ci-steps">
            {steps.map((s, i) => (
              <div key={s} className={`ci-step-dot ${step === s ? 'active' : (steps.indexOf(step) > i ? 'done' : '')}`} />
            ))}
          </div>
        </div>

        {/* Error banner */}
        {error && <div className="ci-error">⚠️ {error}</div>}

        {/* STEP 1: Enter phone */}
        {step === 'phone' && (
          <form className="ci-body" onSubmit={handlePhoneSubmit}>
            <div className="ci-icon-big">📱</div>
            <h2>{t('checkin.enterPhone')}</h2>
            <p className="ci-hint">{t('checkin.phoneHint')}</p>
            <input
              className="ci-input"
              type="tel"
              value={phone}
              onChange={e => setPhone(e.target.value)}
              placeholder={t('checkin.phonePlaceholder')}
              autoFocus
              required
            />
            <button className="ci-btn primary" type="submit" disabled={loading}>
              {loading ? t('checkin.searching') : t('checkin.findBooking')}
            </button>
          </form>
        )}

        {/* STEP 2: Checked-in */}
        {step === 'checkedin' && (
          <div className="ci-body">
            <div className="ci-icon-big ci-pulse">✅</div>
            <h2>{t('checkin.checkedIn')}</h2>
            {booking && (
              <div className="ci-booking-card">
                <div className="ci-booking-row"><span>👤</span><span>{booking.customerName}</span></div>
                <div className="ci-booking-row"><span>💇</span><span>{booking.serviceType || '—'}</span></div>
                <div className="ci-booking-row"><span>👩‍💼</span><span>{booking.staffName || '—'}</span></div>
              </div>
            )}
            <p className="ci-hint">{t('checkin.checkoutHint')}</p>
            <button className="ci-btn success" onClick={() => { setStep('phone'); setPhone(''); setError(null); }}>
              {t('checkin.scanAgain')}
            </button>
          </div>
        )}

        {/* STEP 3: Review */}
        {step === 'review' && (
          <div className="ci-body">
            <div className="ci-icon-big">⭐</div>
            <h2>{t('review.title')}</h2>
            <p className="ci-hint">{t('review.hint')} <strong>{businessName}</strong>?</p>
            <StarRating value={rating} onChange={setRating} />
            <div className="ci-star-label">
              {rating === 0 && `👆 ${t('review.selectStar')}`}
              {rating === 1 && `😞 ${t('review.terrible')}`}
              {rating === 2 && `😕 ${t('review.bad')}`}
              {rating === 3 && `😐 ${t('review.average')}`}
              {rating === 4 && `😊 ${t('review.good')}`}
              {rating === 5 && `🤩 ${t('review.excellent')}`}
            </div>
            <textarea
              className="ci-textarea"
              placeholder={t('review.feedbackPlaceholder')}
              value={feedback}
              onChange={e => setFeedback(e.target.value)}
              rows={3}
            />
            {rating === 5 && googleMapsUrl && (
              <div className="ci-google-hint">
                🗺️ {t('review.googleMapsHint')}
              </div>
            )}
            <button className="ci-btn primary" onClick={handleReviewSubmit} disabled={loading || rating === 0}>
              {loading ? t('review.submitting') : rating === 5 && googleMapsUrl ? t('review.submitGoogle') : t('review.submit')}
            </button>
          </div>
        )}

        {/* STEP 3b: Google Maps prompt (5-star only) */}
        {step === 'google-prompt' && (
          <div className="ci-body ci-google-prompt">
            <div className="ci-icon-big">🌟</div>
            <h2 style={{ color: '#f59e0b' }}>{T('Thank you so much!', 'Cảm ơn bạn rất nhiều!')}</h2>

            <div className="ci-google-card">
              <div className="ci-google-logo">🗺️</div>
              <p style={{ fontWeight: 600, fontSize: '1rem', marginBottom: '0.25rem' }}>
                {T('Share your review on Google Maps?', 'Chia sẻ review lên Google Maps?')}
              </p>
              <p style={{ fontSize: '0.85rem', color: '#777', marginBottom: '0' }}>
                {T('This helps other customers find us!', 'Giúp khách hàng khác tìm bạn!')}
              </p>
            </div>
            <p className="ci-hint" style={{ marginBottom: '0.5rem' }}>
              💡 {T(
                'Your review text will be auto-copied to clipboard. Just paste it in Google Maps and select 5 ⭐',
                'Nội dung review sẽ được sao chép tự động. Chỉ cần dán vào Google Maps và chọn 5 ⭐'
              )}
            </p>
            {feedback && (
              <div className="ci-review-preview">
                <span className="ci-review-preview-label">{T('📋 Content to be copied:', '📋 Nội dung sẽ được copy:')}</span>
                <p>"{feedback}"</p>
              </div>
            )}
            <button
              className={`ci-btn primary ${copied ? 'ci-btn-copied' : ''}`}
              onClick={handleCopyAndOpenMaps}
              disabled={copied}
              style={{ marginBottom: '0.75rem' }}
            >
              {copied ? T('✅ Copied! Opening Google Maps...', '✅ Đã copy! Đang mở Google Maps...') : T('📋 Copy & Open Google Maps', '📋 Copy & Mở Google Maps')}
            </button>
            <button className="ci-btn secondary" onClick={() => setStep('done')}>
              {T('Skip', 'Bỏ qua')}
            </button>
          </div>
        )}

        {/* STEP 4: Done */}
        {step === 'done' && (
          <div className="ci-body ci-done">
            <div className="ci-icon-big">🎉</div>
            <h2>{t('review.thankyou')}</h2>
            <p>{t('review.thankyouMsg')}</p>
            <p className="ci-hint">{t('review.seeYouAgain')} 💇‍♀️</p>
            <button className="ci-btn secondary" onClick={reset}>
              {t('checkin.scanAgain')}
            </button>
          </div>
        )}

        <div className="ci-footer">{t('checkin.poweredBy')} ✨</div>
      </div>
    </div>
  );
};

export default CheckinPage;
